import apiClient from './client';
import type { CheckinOneOnOneType, SessionContentStatus } from '../../types';

// ============================================================================
// MED-702 — Session-number configuration & by-session view API
// ============================================================================

export type SessionNumberConfigModality = 'group_coaching' | 'one_on_one';

export type OneOnOneType =
  | 'standard_1_1'
  | 'two_on_one'
  | '360_debrief'
  | 'hogan_debrief'
  | 'other_assessment_debrief';

export interface SessionNumberConfig {
  id: string;
  cohort_id: string;
  modality: SessionNumberConfigModality;
  session_number: number;
  topic: string | null;
  created_at: string;
  updated_at: string;
}

export interface SessionNumberConfigListResponse {
  configs: SessionNumberConfig[];
  group_coaching_session_numbers: number[];
  one_on_one_session_numbers: number[];
}

export interface PropagationResult {
  config: SessionNumberConfig;
  sessions_updated: number;
  sessions_skipped_due_to_override: number;
  sessions_skipped_due_to_scheduling: number;
}

// ----- By-Session-Number response shapes -----

export interface BySessionGroupRow {
  session_id: string;
  group_id: string;
  group_name: string;
  time_slot_identifier: string | null;
  time_slot_day_of_week: string | null;
  time_slot_time: string | null;
  time_slot_timezone: string | null;
  computed_topic: string | null;
  status: string;
  scheduled_start: string | null;
  scheduled_end: string | null;
  attendance_attended: number;
  attendance_total: number;
  avg_engagement: number | null;
  coach_user_id: string | null;
  coach_name: string | null;
  // MED-954 / E4.0d: per-session peer leader name (E4.0a/E4.0c) and the
  // cohort-level peer_led flag. The "View by Session" GroupRow swaps the Coach
  // column for the Leader name (or "Unassigned") when is_peer_led.
  session_leader_name: string | null;
  is_peer_led: boolean;
  topic_override: string | null;
}

export interface BySessionGroupCoachingEntry {
  session_number: number;
  topic: string | null;
  session_number_topic: string | null;
  week_of: string | null;
  groups_total: number;
  groups_scheduled: number;
  groups_complete: number;
  attendance_attended: number;
  attendance_total: number;
  avg_engagement: number | null;
  /**
   * MED-1086 (E1.3m / AC11): session-number-scoped content-authoring status for
   * the e1.3k badge on the By-Session header row (closing the gap where the
   * badge shipped on By-Date only). Null for not-started numbers.
   */
  content_status: SessionContentStatus | null;
  groups: BySessionGroupRow[];
}

/**
 * MED-1086 (E1.3m / AC11): one entry per session number with its content_status,
 * for the no-groups zero state (where the by-session-number view returns nothing
 * because there are no group sessions to roll up).
 */
export interface SessionNumberContentStatus {
  session_number: number;
  content_status: SessionContentStatus | null;
}

export interface ContentStatusBySessionNumberResponse {
  session_numbers: SessionNumberContentStatus[];
}

export interface BySessionOneOnOneParticipantRow {
  session_id: string;
  participant_user_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  coach_user_id: string | null;
  coach_name: string | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  timezone: string | null;
  status: string;
  one_on_one_type: OneOnOneType | CheckinOneOnOneType | null;
  // MED-1346: completed, but the participant was absent or excused.
  absent?: boolean;
}

export interface BySessionOneOnOneEntry {
  // MED-1350: 'n:<number>' for a numbered entry, or the check-in type.
  entry_key: string;
  // Null for a check-in entry.
  session_number: number | null;
  // 'Intro' or 'Midpoint' for a check-in entry, null for a numbered entry.
  label: string | null;
  checkin_type: CheckinOneOnOneType | null;
  target_week: number | null;
  session_type: OneOnOneType | CheckinOneOnOneType | null;
  participants_total: number;
  participants_scheduled: number;
  participants_completed: number;
  participants: BySessionOneOnOneParticipantRow[];
}

export type BySessionNumberResponse =
  | { modality: 'group_coaching'; session_numbers: BySessionGroupCoachingEntry[] }
  | { modality: 'one_on_one'; session_numbers: BySessionOneOnOneEntry[] };

// ----- API methods -----

export const sessionNumberConfigsApi = {
  list: async (cohortId: string): Promise<SessionNumberConfigListResponse> => {
    return apiClient.get<SessionNumberConfigListResponse>(
      `/api/cohorts/${cohortId}/session-number-configs`,
    );
  },

  getBySessionNumber: async (
    cohortId: string,
    modality: SessionNumberConfigModality,
    coachUserId?: string,
  ): Promise<BySessionNumberResponse> => {
    const qs = new URLSearchParams({ modality });
    if (coachUserId) qs.set('coachUserId', coachUserId);
    return apiClient.get<BySessionNumberResponse>(
      `/api/cohorts/${cohortId}/sessions/by-session-number?${qs.toString()}`,
    );
  },

  /**
   * MED-1086 (E1.3m / AC11) — per-session-number content_status for the cohort's
   * group_coaching modality, independent of group sessions. Powers the
   * content-status badge on the no-groups zero state.
   */
  getContentStatusBySessionNumber: async (
    cohortId: string,
  ): Promise<ContentStatusBySessionNumberResponse> => {
    return apiClient.get<ContentStatusBySessionNumberResponse>(
      `/api/cohorts/${cohortId}/sessions/content-status`,
    );
  },

  setGroupCoachingTopic: async (
    cohortId: string,
    sessionNumber: number,
    topic: string | null,
  ): Promise<PropagationResult> => {
    return apiClient.put<PropagationResult>(
      `/api/cohorts/${cohortId}/session-number-configs/group-coaching/topic`,
      { session_number: sessionNumber, topic },
    );
  },

  bulkSetGroupCoachingTopics: async (
    cohortId: string,
    topics: Array<{ session_number: number; topic: string | null }>,
  ): Promise<{ configs: SessionNumberConfig[] }> => {
    return apiClient.put<{ configs: SessionNumberConfig[] }>(
      `/api/cohorts/${cohortId}/session-number-configs/group-coaching/bulk-topics`,
      { topics },
    );
  },

  propagateOneOnOneType: async (
    cohortId: string,
    sessionNumber: number,
    oneOnOneType: OneOnOneType,
  ): Promise<PropagationResult> => {
    return apiClient.put<PropagationResult>(
      `/api/cohorts/${cohortId}/session-number-configs/one-on-one/type`,
      { session_number: sessionNumber, one_on_one_type: oneOnOneType },
    );
  },

  setSessionTopicOverride: async (
    cohortId: string,
    sessionId: string,
    topicOverride: string | null,
  ): Promise<void> => {
    await apiClient.put<{ session_id: string; topic_override: string | null }>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/topic-override`,
      { topic_override: topicOverride },
    );
  },
};

export const ONE_ON_ONE_TYPE_LABELS: Record<OneOnOneType, string> = {
  standard_1_1: 'Standard 1:1',
  two_on_one: '2:1 (with Manager)',
  '360_debrief': '360 Debrief',
  hogan_debrief: 'Hogan Debrief',
  other_assessment_debrief: 'Other Assessment Debrief',
};

export default sessionNumberConfigsApi;
